import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";
import RewardAdModal from "@/components/RewardAdModal";
import { Gift, Flower2, Coffee, Heart, Gem, Clock } from "lucide-react";
import { toast } from "sonner";

const ICONS = { Flower: Flower2, Coffee, Heart, Gem, Gift };

export default function GiftCarousel({ open, onClose, character, onReaction }) {
  const [catalog, setCatalog] = useState([]);
  const [status, setStatus] = useState({ remaining: 2, next_reset: null });
  const [pending, setPending] = useState(null);
  const [countdown, setCountdown] = useState("");

  const refresh = () => {
    api.get("/gifts/catalog").then(({ data }) => setCatalog(data));
    api.get("/gifts/status").then(({ data }) => setStatus(data));
  };
  useEffect(() => { if (open) refresh(); }, [open]);

  useEffect(() => {
    if (!status.next_reset) { setCountdown(""); return; }
    const t = setInterval(() => {
      const diff = new Date(status.next_reset) - new Date();
      if (diff <= 0) { setCountdown(""); refresh(); return; }
      const h = String(Math.floor(diff / 3.6e6)).padStart(2, "0");
      const m = String(Math.floor((diff % 3.6e6) / 6e4)).padStart(2, "0");
      const s = String(Math.floor((diff % 6e4) / 1000)).padStart(2, "0");
      setCountdown(`${h}:${m}:${s}`);
    }, 1000);
    return () => clearInterval(t);
  }, [status.next_reset]);

  const choose = (g) => {
    if (status.remaining <= 0) { toast.error("Gift cooldown active"); return; }
    setPending(g);
  };

  const completeGift = async () => {
    const g = pending;
    setPending(null);
    try {
      const { data } = await api.post("/gifts/send", { character_id: character.id, gift: g.name });
      onReaction && onReaction(data);
      toast.success(`${character.name} loved the ${g.name}! 🥰`);
      refresh();
    } catch (e) {
      toast.error(e.response?.status === 429 ? "Gift cooldown active" : "Could not send gift");
    }
  };

  const locked = status.remaining <= 0;

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="bg-surface border-white/10 rounded-3xl max-w-md" data-testid="gift-carousel">
          <DialogTitle className="font-heading text-xl font-bold flex items-center gap-2">
            <Gift className="w-5 h-5 text-brand" /> Send a Gift
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {locked ? "Daily gift limit reached." : `${status.remaining} of ${status.limit} gifts left today.`}
          </DialogDescription>

          {locked ? (
            <div className="py-8 text-center" data-testid="gift-cooldown">
              <Clock className="w-10 h-10 text-brand mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">Companion gift cooldown active.</p>
              <p className="font-heading text-2xl font-bold mt-1 tracking-wider">{countdown || "--:--:--"}</p>
            </div>
          ) : (
            <div className="relative h-72 mt-2 mb-1" data-testid="gift-wheel">
              <div className="absolute left-1/2 top-1/2 w-28 h-28 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/10 plasma opacity-20" />
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[10px] uppercase tracking-[0.25em] text-muted-foreground">tap a gift</div>
              {catalog.map((g, i) => {
                const Icon = ICONS[g.icon] || Gift;
                const a = (i / catalog.length) * Math.PI * 2 - Math.PI / 2;
                const R = 100;
                const x = Math.cos(a) * R, y = Math.sin(a) * R;
                return (
                  <div key={g.id} className="absolute"
                    style={{ left: "50%", top: "50%", transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))` }}>
                    <button data-testid={`gift-${g.name.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => choose(g)}
                      className="w-24 flex flex-col items-center gap-1.5 p-3 rounded-2xl border border-white/10 bg-slate-900/60 backdrop-blur-md shadow-[0_8px_30px_rgba(0,0,0,0.5)] hover:scale-110 hover:border-cyan-400/70 transition-transform duration-300">
                      <Icon className="w-7 h-7 text-cyan-300" />
                      <span className="text-xs font-medium text-center leading-tight">{g.name}</span>
                      <span className="text-[10px] text-brand">+{g.trust_boost}</span>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          <p className="text-[11px] text-muted-foreground/60 text-center mt-2">Watch a short ad to unlock each gift. Max 2 / 24h.</p>
        </DialogContent>
      </Dialog>

      <RewardAdModal open={!!pending} seconds={30} title="Watch to unlock your gift"
        slotId="ad-slot-gift-reward" onComplete={completeGift} onClose={() => setPending(null)} />
    </>
  );
}
